import React, {FC, memo, useEffect, useState} from "react";
import {Badge, Button, Divider, Modal, Space, Table, Tooltip} from "antd";
import {ColumnProps} from "antd/lib/table";
import moment from "moment";
import {CloseOutlined, InfoCircleOutlined, InfoCircleTwoTone} from "@ant-design/icons";
import {gold, green, grey, orange, red} from "@ant-design/colors";
import FileUploadComponent /* , {SingleUploadResult} */ from "./FileUploadComponent";
import ImportSummary from "./ImportSummary";
import {renderAmount, renderBooleanAsCheckmark} from "../../../views/TableColumnRenderers/TableColumnRenderers";
import {SwissQRFileUploadResult} from "../../../../server/swissQRFile/swissQRFile.inputTypes";
import SettlementDetails from "../../invoice/SettlementDetails";
import {useGetCompanyInfoQuery} from "../../../generated/graphql";
import SettledLessonBookingDetails from "../../bookings/lessonBooking/SettledLessonBooking/SettledLessonBookingDetails";

type ErrorDetail = {
    msg: string;
    description: string;
};

export type KnownError = {
    overpaid: ErrorDetail;
    underpaid: ErrorDetail;
    paid: ErrorDetail;
    ambiguous: ErrorDetail;
    "error - invoice not found": ErrorDetail;
    "error - duplicate transaction": ErrorDetail;
};

const knownError: KnownError = {
    overpaid: {
        msg: "Überbezahlt",
        description: "Der Betrag ist zu hoch. Der Differenzbetrag wird als Guthaben verbucht.",
    },
    underpaid: {
        msg: "Unterbezahlt",
        description: "Der Betrag ist zu niedrig und wird als Teilzahlung verbucht.",
    },
    paid: {msg: "Bezahlt", description: "Der Betrag stimmt überein"},
    ambiguous: {
        msg: "Zu klären",
        description: "Nicht verbucht. Bitte in der Import-Historie über „Klären“ abschließen.",
    },
    "error - invoice not found": {
        msg: "Rechnung nicht gefunden",
        description: "Kann keiner Rechnung zugeordnet werden und wird daher ignoriert.",
    },
    "error - duplicate transaction": {
        msg: "Doppelte Transaktion",
        description: "Diese Transaktion kommt mehrfach in der Datei vor und wird daher ignoriert.",
    },
};

export type TableType = SwissQRFileUploadResult["records"][number] & {
    openAmount: any;
};

type SwissQrCamtFileUploadProps = {
    setModalOpen: (open: boolean) => void;
};

const SwissQrCamtFileUpload: FC<SwissQrCamtFileUploadProps> = ({setModalOpen}) => {
    const {data: companyInfoData} = useGetCompanyInfoQuery();
    const featureLessonBooking = companyInfoData?.companyInfo?.subscription?.featureLessonBooking;
    const [result, setResult] = useState<Array<TableType>>();
    const [uploadPhase, setUploadPhase] = useState<"select" | "preview" | "summary">("select");

    useEffect(() => {
        // result updated
    }, [result]);

    const ResultBadge = ({result}: {result: keyof KnownError | string}) => {
        const isKnownErrorKey = (key: string): key is keyof KnownError => {
            return key in knownError;
        };

        if (!isKnownErrorKey(result)) {
            return <>{result}</>;
        }

        const badgeColor =
            result === "paid"
                ? green[3]
                : result === "overpaid"
                ? orange[3]
                : result === "underpaid"
                ? red[3]
                : result === "ambiguous"
                ? gold[3]
                : grey[0];

        return (
            <Tooltip title={knownError[result].description}>
                <Space size={5}>
                    <InfoCircleTwoTone />
                    <Badge count={knownError[result].msg} color={badgeColor} />
                </Space>
            </Tooltip>
        );
    };

    const BookingDetails = ({record}: {record: TableType}) => {
        const [modalOpen, setModalOpen] = useState(false);

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!record.scheduledPayment) {
            return <>{"-"}</>;
        }

        const bookingId = record.scheduledPayment.bookingId;

        return (
            <>
                <Modal
                    title="Details zur Buchung"
                    open={modalOpen}
                    onCancel={() => setModalOpen(false)}
                    width={1220}
                    style={{top: 20}}
                    bodyStyle={{
                        maxHeight: "calc(100vh - 160px)",
                        // maxHeight: "calc(100% - 100px)",
                        overflowY: "auto",
                    }}
                    okButtonProps={{style: {display: "none"}}}
                    cancelButtonProps={{
                        type: "primary",
                        icon: <CloseOutlined />,
                    }}
                    cancelText="Ansicht schliessen"
                >
                    {/* Gleiche Weiche wie Bookings.tsx und RenderBookingNumber: Einzelbuchungs-Ansicht nur mit Feature */}
                    {featureLessonBooking ? (
                        <SettledLessonBookingDetails bookingId={bookingId} renderWithoutPage />
                    ) : (
                        <SettlementDetails bookingId={bookingId} renderWithoutPage />
                    )}
                </Modal>
                <Button data-testid="bookingDetails" size="small" onClick={() => setModalOpen(!modalOpen)}>
                    <InfoCircleOutlined style={{color: "rgb(7, 96, 255)"}} />
                </Button>
            </>
        );
    };

    const PreviewTable = () => {
        const columns: Array<ColumnProps<TableType>> = [
            // const columns: Array<ColumnProps<SingleUploadRecord>> = [
            // {
            //     dataIndex: "id",
            //     title: "ID",
            //     render: (text, record: TableType) => ({
            //         displayName: "ID",
            //         children: <>{text}</>,
            //     }),
            // },
            {
                dataIndex: "amount",
                title: "Eingegangener Betrag",
                render: (text, record: TableType) => ({
                    displayName: "amount",
                    children: <>{renderAmount(text)}</>,
                }),
            },
            {
                dataIndex: "openAmount",
                title: "Offener Betrag",
                render: (text, record: TableType) => ({
                    displayName: "openAmount",
                    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                    children: record.scheduledPayment
                        ? renderAmount(record.scheduledPayment.openAmount ?? record.scheduledPayment.paymentAmount)
                        : "-",
                }),
            },
            {
                dataIndex: "valutaDate",
                title: "Valuta",
                render: (text, record: TableType) => ({
                    displayName: "valutaDate",
                    children: <>{moment(text).format("L")}</>,
                }),
            },

            {
                dataIndex: "swissQrRef",
                title: "QR-Referenz",
                render: (text, record: TableType) => ({
                    displayName: "swissQrRef",
                    children: <>{text}</>,
                }),
            },
            {
                dataIndex: "successful",
                title: "Valide",
                render: (text, record: TableType) => ({
                    displayName: "successful",
                    children: <>{renderBooleanAsCheckmark(text)}</>,
                }),
            },
            {
                dataIndex: "result",
                title: "Ergebnis",
                render: (text, record: TableType) => ({
                    displayName: "result",
                    // children: <ResultBadge result={text as keyof KnownError} />,
                    children: <ResultBadge result={text} />,
                }),
            },
            {
                dataIndex: "bookingDetails",
                title: "Buchungsdetails",
                align: "right",
                render: (text, record: TableType) => ({
                    displayName: "bookingDetails",
                    children: <BookingDetails record={record} />,
                }),
            },
        ];

        if (!result) return null;

        return <Table<TableType> size="small" columns={columns} dataSource={result} />;
    };

    const handleLiftedResult = (liftedResult: Array<TableType> | undefined) => {
        setResult(liftedResult);
        if (liftedResult && uploadPhase === "select") {
            setUploadPhase("preview");
        }
    };

    const handleUploadComplete = () => {
        setUploadPhase("summary");
    };

    if (uploadPhase === "summary" && result) {
        return <ImportSummary result={result} onClose={() => setModalOpen(false)} />;
    }

    return (
        <>
            {result && <Divider orientation="left">{"Vorschau"}</Divider>}
            <PreviewTable />
            <FileUploadComponent
                liftedResult={handleLiftedResult}
                onUploadComplete={handleUploadComplete}
            />
        </>
    );
};

export default memo(SwissQrCamtFileUpload);
